import React, { useState, useRef, useEffect } from 'react';
import FileUploader from '../Upload/FileUploader';
import { extractBookTables, getBookTablesStatus, downloadBookTablesExcel } from '../../services/bookTables';

const POLL_INTERVAL_MS = 2500;

const PHASE_LABELS = {
  extracting: 'Extrayendo tablas',
  merging: 'Fusionando tablas partidas entre páginas',
  building_excel: 'Generando Excel',
};

function BookTablesView() {
  const [file, setFile] = useState(null);
  const [taskId, setTaskId] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    return () => clearInterval(pollRef.current);
  }, []);

  const handleFileSelect = async (selectedFile) => {
    setFile(selectedFile);
    setError(null);
    setStatus(null);
    try {
      const { task_id: newTaskId } = await extractBookTables(selectedFile);
      setTaskId(newTaskId);
      pollRef.current = setInterval(async () => {
        try {
          const current = await getBookTablesStatus(newTaskId);
          setStatus(current);
          if (current.state === 'SUCCESS' || current.state === 'FAILURE') {
            clearInterval(pollRef.current);
          }
        } catch (err) {
          clearInterval(pollRef.current);
          setError(err.message);
        }
      }, POLL_INTERVAL_MS);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const baseName = file ? file.name.replace(/\.pdf$/i, '') : 'libro';
      await downloadBookTablesExcel(taskId, `${baseName}_tablas.xlsx`);
    } catch (err) {
      setError(err.message);
    } finally {
      setDownloading(false);
    }
  };

  const handleReset = () => {
    clearInterval(pollRef.current);
    setFile(null);
    setTaskId(null);
    setStatus(null);
    setError(null);
  };

  const renderProgress = () => {
    const meta = status?.meta || {};
    const label = PHASE_LABELS[meta.phase] || 'Procesando';
    const chunkInfo = meta.total_chunks ? ` (bloque ${meta.chunk}/${meta.total_chunks})` : '';
    const tablesInfo = meta.tables_found != null ? ` — ${meta.tables_found} tablas encontradas` : '';
    const percent = meta.total_chunks ? Math.round((meta.chunk / meta.total_chunks) * 100) : null;

    return (
      <div className="book-progress">
        <div className="spinner"></div>
        <p>{label}{chunkInfo}{tablesInfo}</p>
        {percent != null && (
          <div className="book-progress-bar">
            <div className="book-progress-fill" style={{ width: `${percent}%` }}></div>
          </div>
        )}
      </div>
    );
  };

  const renderResult = () => {
    const result = status.result || {};
    return (
      <div className="book-result">
        <h3>Extracción completada</h3>
        <ul>
          <li>Tablas detectadas: {result.tables_extracted}</li>
          <li>Tablas tras fusionar continuaciones: {result.tables_after_merge}</li>
          <li>Pestañas en el Excel: {result.sheets}</li>
          {result.skipped > 0 && <li>Fragmentos descartados (muy pequeños): {result.skipped}</li>}
        </ul>
        <button className="export-button" onClick={handleDownload} disabled={downloading}>
          {downloading ? 'Descargando...' : 'Descargar Excel'}
        </button>
      </div>
    );
  };

  return (
    <div className="book-tables-view">
      {!file ? (
        <FileUploader onFileSelect={handleFileSelect} />
      ) : (
        <div className="book-tables-panel">
          <p className="book-filename">{file.name}</p>
          {error && (
            <div className="error-container">
              <p>{error}</p>
            </div>
          )}
          {status?.state === 'FAILURE' && (
            <div className="error-container">
              <p>Error procesando el documento: {status.error}</p>
            </div>
          )}
          {!error && status?.state !== 'SUCCESS' && status?.state !== 'FAILURE' && renderProgress()}
          {status?.state === 'SUCCESS' && renderResult()}
          <button className="reset-button" onClick={handleReset}>
            Nuevo documento
          </button>
        </div>
      )}

      <style>{`
        .book-tables-view {
          max-width: 720px;
          margin: 0 auto;
        }
        .book-tables-panel {
          background: var(--card-background);
          border-radius: var(--border-radius);
          box-shadow: var(--shadow);
          padding: 32px;
          display: flex;
          flex-direction: column;
          gap: 20px;
          align-items: center;
        }
        .book-filename {
          font-weight: 600;
          font-size: 15px;
        }
        .book-progress {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
          width: 100%;
        }
        .book-progress-bar {
          width: 100%;
          height: 8px;
          background: #e5e7eb;
          border-radius: 4px;
          overflow: hidden;
        }
        .book-progress-fill {
          height: 100%;
          background: var(--primary-color);
          transition: width 0.4s ease;
        }
        .book-result {
          text-align: center;
          display: flex;
          flex-direction: column;
          gap: 12px;
          align-items: center;
        }
        .book-result ul {
          list-style: none;
          padding: 0;
          color: var(--text-secondary);
          font-size: 14px;
        }
      `}</style>
    </div>
  );
}

export default BookTablesView;
